<template>
  <div
    id="product-page"
    :class="['product-page', {
      'product-page--with-background': hasBackground,
      'product-page--with-module': $slots['module'],
      'product-page--with-module--with-usps': hasUsps,
      'product-page--with-full-width-product-module': $slots['module'] && fullWidthProductModule
    }]"
  >
    <div class="inner">
      <zg-tooltip
        :visible="!!tooltipText"
        :text="tooltipText"
        hide-trigger
        single-line
      />

      <zg-breadcrumb
        v-if="breadcrumbs.length"
        :bread-crumbs="breadcrumbs"
        text-color="black"
        class="breadcrumbs"
      />

      <h1
        v-if="title"
        v-html="title"
      />

      <div
        v-if="$slots['sub-title']"
        class="sub-title"
      >
        <slot name="sub-title" />
      </div>

      <div
        v-if="$slots['module']"
        class="module"
      >
        <slot name="module" />
      </div>

      <div
        v-else-if="$slots['content']"
        class="content"
      >
        <slot name="content" />
      </div>
    </div>

    <div
      v-if="hasUsps"
      class="usps"
    >
      <zg-tips-line
        size="small"
        :tips="usps"
      />
    </div>
  </div>
</template>

<script>
  import ZgBreadcrumb from '../ZgBreadCrumb/ZgBreadCrumb'
  import ZgTipsLine from '../ZgTipsLine/ZgTipsLine'
  import ZgTooltip from '../ZgTooltip/ZgTooltip'

  export default {
    name: 'ProductPage',
    components: {
      ZgBreadcrumb,
      ZgTipsLine,
      ZgTooltip
    },
    props: {
      title: {
        type: String,
        required: false,
        default: null
      },
      usps: {
        type: Array,
        default: null
      },
      tooltipText: {
        type: String,
        default: ''
      },
      hasBackground: {
        type: Boolean,
        required: false,
        default: false
      },
      breadcrumbs: {
        type: Array,
        required: false,
        default: null
      },
      fullWidthProductModule: {
        type: Boolean,
        required: false,
        default: false
      }
    },
    computed: {
      hasUsps () {
        return !!(this.usps && this.usps.length)
      }
    }
  }
</script>

<style lang="scss" scoped>
  $content-max-width: 1176px; // 1080px plus 48px padding on each side

  .product-page {
    position: relative;
    width: 100%;
    min-height: rem(240px);
    padding: rem(32px) rem(16px);

    .inner {
      max-width: rem(1080px);
      display: flex;
      flex-direction: column;
      margin: 0 auto;

      ::v-deep .zg-tooltip {
        right: rhythm();
        bottom: calc(#{rhythm(xsmall)} / 2);

        @include mq(large) {
          right: 0;
        }
      }
    }

    .breadcrumbs {
      margin: 0 0 rem(12px);
    }

    h1 {
      @include type-title-xl;

      color: color(white);
      margin: 0 0 rem(12px);

      @include mq(medium) {
        @include type-title-xxl;

        margin: 0 0 rem(12px);
      }

      @include mq(large) {
        font-size: rem(96px);
        line-height: rem(104px);
        margin: 0 0 rem(12px);
      }
    }

    .sub-title {
      @include type-title-m;

      color: color(white);
      font-weight: font-weight(bold);
      margin: 0 0 rem(24px);

      ::v-deep p {
        @include type-title-m;

        color: color(white);
        font-weight: font-weight(bold);
        margin: 0 0 rem(8px);

        &:last-child {
          margin: 0;
        }
      }
    }
  }

  // ? ----------------------------------------------------------------------
  // modifiers
  // ? ----------------------------------------------------------------------

  .product-page--with-background {
    position: relative;
    padding: rem(32px) rem(16px) rem(24px);
    margin: rem(145px) auto 0;
    border-radius: rem(32px) rem(32px) 0 0;
    background-color: color(white);

    @include mq(medium) {
      margin: rem(256px) auto 0;
    }

    @include mq($content-max-width) {
      max-width: $content-max-width;
      padding: rem(48px) rem(48px) rem(24px);
      margin: rem(420px) auto 0;
      border-radius: rem(48px) rem(48px) 0 0;
    }

    .inner {
      max-width: rem(1080px);
      display: flex;
      flex-direction: column;
      margin: 0 auto;
    }

    .breadcrumbs {
      margin: 0 0 rem(12px);

      @include mq(medium) {
        margin: 0 0 rem(12px);
      }

      @include mq(large) {
        margin: 0 0 rem(12px);
      }
    }

    h1 {
      @include type-title-xl;

      color: color(black);
      margin: 0 0 rem(12px);

      @include mq(medium) {
        @include type-title-xxl;

        margin: 0 0 rem(12px);
      }

      @include mq(large) {
        @include type-title-xxl;

        margin: 0 0 rem(12px);
      }
    }

    .sub-title {
      @include type-body-m;

      color: color(black);
      margin: 0 0 rem(24px);

      ::v-deep p {
        @include type-body-m;

        color: color(black);
        margin: 0 0 rem(8px);

        &:last-child {
          margin: 0;
        }
      }
    }
  }

  .product-page--with-module {
    max-width: rem(1080px);
    min-height: rem(208px);
    background-color: transparent;
    padding: 0;
    margin: 0 auto;

    @include mq(medium) {
      min-height: rem(336px);
      margin: 0 auto;
    }

    @include mq($content-max-width) {
      max-width: rem(1080px);
      min-height: rem(558px);
      padding: 0;
      margin: 0 auto;
    }

    .inner {
      background-color: color(white);
      padding: rem(32px) rem(16px);
      margin: rem(24px) 0 0;
      border-radius: rem(32px) rem(32px) 0 0;
      height: min-content;

      @include mq(medium) {
        margin: rem(256px) 0 0;
        padding: rem(32px) rem(16px);
      }

      @include mq(large) {
        width: rem(610px);
        padding: rem(32px);
        margin: rem(38px) 0 rem(24px);
        border-radius: rem(32px);
      }
    }

    h1 {
      @include type-title-l;

      color: color(black);
      margin: 0 0 rem(12px);

      @include mq(medium) {
        @include type-title-l;

        margin: 0 0 rem(12px);
      }

      @include mq(large) {
        @include type-title-l;

        margin: 0 0 rem(12px);
      }
    }

    .sub-title {
      @include type-body-m;

      color: color(black);
      margin: 0 0 rem(24px);

      ::v-deep p {
        @include type-body-m;

        color: color(black);
        margin: 0 0 rem(8px);

        &:last-child {
          margin: 0;
        }
      }
    }

    .module {
      min-height: rem(60px);

      ::v-deep > div {
        margin: 0;
      }
    }

    &--with-usps#product-page {
      padding-top: rhythm(medium);

      @include mq("large") {
        display: grid;
        grid-template-columns: 1fr 1fr;
        justify-content: space-between;
        column-gap: rhythm(large);
        padding-bottom: rhythm(medium);
      }
    }
  }

  .product-page--with-full-width-product-module {
    .inner {
      @include mq(large) {
        width: 100%;
      }
    }
  }

  .usps {
    background-color: color(white);

    @include mq(large) {
      margin: rhythm(5) rhythm();
      background-color: unset;
    }
  }
</style>
